import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Gerenciador de Infinite Scroll para simular o carregamento assíncrono de um array com grande volumes de dados
 */
export class InfiniteScrollManager<T> {
    private allItems: T[] = [];
    private currentPage = 0;
    private itemsPerPage = 20;

    // BehaviorSubject para emitir os itens visíveis atualmente
    private itemsSubject = new BehaviorSubject<T[]>([]);

    // Flag para indicar se todos os itens foram carregados
    private _done = new BehaviorSubject<boolean>(false);

    // Getters para os observables que serão consumidos no template
    public get items$(): Observable<T[]> {
        return this.itemsSubject.asObservable();
    }

    public get done$(): Observable<boolean> {
        return this._done.asObservable();
    }

    /**
     * Construtor do gerenciador de infinite scroll
     *
     * @param items Array completo de itens a serem gerenciados
     * @param itemsPerPage Quantidade de itens a serem carregados por página
     */
    constructor(items: T[], itemsPerPage: number = 20) {
        this.allItems = [...items];
        this.itemsPerPage = itemsPerPage;

        // Inicializa com a primeira página de itens
        this.loadNextPage();
    }

    /**
     * Método para carregar a próxima página de itens
     * Este método deve ser chamado quando o usuário rolar até o final da página
     *
     * @returns boolean indicando se ainda existem mais itens para carregar
     */
    public loadNextPage(): boolean {
        // Calcula o índice inicial e final para a próxima página
        const startIndex = this.currentPage * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;

        // Verifica se já chegamos ao final do array
        if (startIndex >= this.allItems.length) {
            this._done.next(true);
            return false;
        }

        // Obtém os itens da nova página
        const newPageItems = this.allItems.slice(startIndex, endIndex);

        // Obtém os itens atuais do BehaviorSubject
        const currentItems = this.itemsSubject.getValue();

        // Junta os itens atuais com os novos e atualiza o BehaviorSubject
        const updatedItems = [...currentItems, ...newPageItems];
        this.itemsSubject.next(updatedItems);

        // Incrementa o contador de página
        this.currentPage++;

        // Verifica se esta foi a última página
        if (endIndex >= this.allItems.length) {
            this._done.next(true);
        }

        return true;
    }

    /**
     * Reset do gerenciador para começar novamente com outro conjunto de itens
     *
     * @param items Novo array de itens
     */
    public reset(items: T[]): void {
        this.allItems = [...items];
        this.currentPage = 0;
        this.itemsSubject.next([]);
        this._done.next(false);
        this.loadNextPage();
    }

    /**
     * Retorna o número total de itens no array original
     */
    public get totalItems(): number {
        return this.allItems.length;
    }

    /**
     * Retorna o número de itens carregados atualmente
     */
    public get loadedItemsCount(): number {
        return this.itemsSubject.getValue().length;
    }

    /**
     * Verifica se todos os itens foram carregados
     */
    public get isDone(): boolean {
        return this._done.getValue();
    }

    /**
     * Adiciona um novo item ao início do array
     *
     * @param item Item a ser adicionado
     * @returns boolean indicando se o item foi adicionado com sucesso
     */
    public addItem(item: T): boolean {
        try {
            // Adiciona o item no início do array completo
            this.allItems.unshift(item);

            // Adiciona o item no início dos itens visíveis
            const currentItems = this.itemsSubject.getValue();
            currentItems.unshift(item);
            this.itemsSubject.next([...currentItems]);

            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Atualiza um item específico no array usando uma função de comparação
     *
     * @param item Item atualizado
     * @param compareFunction Função que define como comparar os itens (ex: (a, b) => a.id === b.id)
     * @returns boolean indicando se o item foi encontrado e atualizado
     */
    public updateItem(item: T, compareFunction: (a: T, b: T) => boolean): boolean {
        const index = this.allItems.findIndex(i => compareFunction(i, item));
        if (index === -1) {
return false;
}

        this.allItems[index] = item;
        const currentItems = this.itemsSubject.getValue();
        const visibleIndex = currentItems.findIndex(i => compareFunction(i, item));

        if (visibleIndex !== -1) {
            currentItems[visibleIndex] = item;
            this.itemsSubject.next([...currentItems]);
        }

        return true;
    }

    /**
     * Remove um item específico do array usando uma função de comparação
     *
     * @param item Item a ser removido
     * @param compareFunction Função que define como comparar os itens (ex: (a, b) => a.id === b.id)
     * @returns boolean indicando se o item foi encontrado e removido
     */
    public removeItem(item: T, compareFunction: (a: T, b: T) => boolean): boolean {
        const index = this.allItems.findIndex(i => compareFunction(i, item));
        if (index === -1) {
return false;
}

        this.allItems.splice(index, 1);
        const currentItems = this.itemsSubject.getValue();
        const visibleIndex = currentItems.findIndex(i => compareFunction(i, item));

        if (visibleIndex !== -1) {
            currentItems.splice(visibleIndex, 1);
            this.itemsSubject.next([...currentItems]);
        }

        return true;
    }
}
